import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, GripVertical, Image, Upload, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiRequest, uploadFile, API_URL } from '../../lib/utils';
import { toast } from 'sonner';

const CATEGORIES = ['Full Dentures', 'Partial Dentures', 'Immediate Dentures', 'Denture Repairs', 'Other'];

export default function AdminGallery() {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);
  const [formData, setFormData] = useState({
    before_image: '',
    after_image: '',
    description: '',
    category: '',
    order: 0,
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const data = await apiRequest('/admin/gallery');
      setGallery(data);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await apiRequest(`/admin/gallery/${editingItem.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Gallery item updated');
      } else {
        await apiRequest('/admin/gallery', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Gallery item created');
      }
      fetchGallery();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save gallery item');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this gallery item?')) return;
    try {
      await apiRequest(`/admin/gallery/${itemId}`, { method: 'DELETE' });
      setGallery(gallery.filter(g => g.id !== itemId));
      toast.success('Gallery item deleted');
    } catch (error) {
      toast.error('Failed to delete gallery item');
    }
  };

  const openEditDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        before_image: item.before_image,
        after_image: item.after_image,
        description: item.description,
        category: item.category || '',
        order: item.order || 0,
      });
    } else {
      setEditingItem(null);
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      before_image: '',
      after_image: '',
      description: '',
      category: '',
      order: gallery.length,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-gallery">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Before & After Gallery
          </h1>
          <p className="text-slate-500 mt-1">{gallery.length} items</p>
        </div>
        <Button onClick={() => openEditDialog()} className="bg-orange-600 hover:bg-orange-700" data-testid="add-gallery-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Gallery Grid */}
      {gallery.length === 0 ? (
        <Card className="border-none shadow-sm">
          <CardContent className="py-16 text-center">
            <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No gallery items yet</p>
            <Button onClick={() => openEditDialog()} className="mt-4 bg-orange-600 hover:bg-orange-700">
              Add First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gallery.map((item) => (
            <Card key={item.id} className="border-none shadow-sm overflow-hidden group" data-testid={`gallery-item-${item.id}`}>
              <div className="relative aspect-square">
                <div className="absolute inset-0 grid grid-cols-2">
                  <div className="overflow-hidden">
                    <img
                      src={item.before_image}
                      alt="Before"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                      Before
                    </span>
                  </div>
                  <div className="overflow-hidden">
                    <img
                      src={item.after_image}
                      alt="After"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-2 right-2 bg-orange-600/90 text-white text-xs px-2 py-1 rounded">
                      After
                    </span>
                  </div>
                </div>
                {/* Overlay actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openEditDialog(item)}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="font-medium text-slate-900">{item.description}</p>
                {item.category && (
                  <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                    {item.category}
                  </span>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Gallery Item' : 'Add Gallery Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Before Image */}
            <div>
              <Label htmlFor="before_image">Before Image</Label>
              <div className="mt-1 space-y-2">
                {formData.before_image && (
                  <div className="relative w-full h-32 bg-slate-100 rounded-lg overflow-hidden">
                    <img 
                      src={formData.before_image.startsWith('/api') ? `${API_URL.replace('/api', '')}${formData.before_image}` : formData.before_image}
                      alt="Before" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, before_image: '' }))}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={beforeInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingBefore(true);
                      try {
                        const result = await uploadFile(file);
                        setFormData(prev => ({ ...prev, before_image: result.url }));
                        toast.success('Before image uploaded');
                      } catch (error) {
                        toast.error('Failed to upload image');
                      } finally {
                        setUploadingBefore(false);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => beforeInputRef.current?.click()}
                    disabled={uploadingBefore}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingBefore ? 'Uploading...' : 'Upload Before'}
                  </Button>
                </div>
                <Input
                  id="before_image"
                  value={formData.before_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, before_image: e.target.value }))}
                  placeholder="Or paste URL..."
                />
              </div>
            </div>
            
            {/* After Image */}
            <div>
              <Label htmlFor="after_image">After Image</Label>
              <div className="mt-1 space-y-2">
                {formData.after_image && (
                  <div className="relative w-full h-32 bg-slate-100 rounded-lg overflow-hidden">
                    <img 
                      src={formData.after_image.startsWith('/api') ? `${API_URL.replace('/api', '')}${formData.after_image}` : formData.after_image}
                      alt="After" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, after_image: '' }))}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={afterInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingAfter(true);
                      try {
                        const result = await uploadFile(file);
                        setFormData(prev => ({ ...prev, after_image: result.url }));
                        toast.success('After image uploaded');
                      } catch (error) {
                        toast.error('Failed to upload image');
                      } finally {
                        setUploadingAfter(false);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => afterInputRef.current?.click()}
                    disabled={uploadingAfter}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingAfter ? 'Uploading...' : 'Upload After'}
                  </Button>
                </div>
                <Input
                  id="after_image"
                  value={formData.after_image}
                  onChange={(e) => setFormData(prev => ({ ...prev, after_image: e.target.value }))}
                  placeholder="Or paste URL..."
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
